<template>
  <div :class="wrapperClasses">
    <label :for="toggleId" class="ui-toggle__label" @click="handleLabelClick">
      <input
        :id="toggleId"
        type="checkbox"
        :checked="modelValue"
        :disabled="disabled"
        :required="required"
        :class="toggleClasses"
        @change="handleChange"
        @blur="handleBlur"
        @focus="handleFocus"
      />
      <span class="ui-toggle__switch">
        <span class="ui-toggle__slider"></span>
      </span>
      <span v-if="label" class="ui-toggle__text">{{ label }}</span>
    </label>
    <div v-if="error || helperText" class="ui-toggle__helper">
      <span v-if="error" class="ui-toggle__error">{{ error }}</span>
      <span v-else-if="helperText" class="ui-toggle__helper-text">{{ helperText }}</span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import type { Size } from "./types";

interface Props {
  modelValue?: boolean;
  label?: string;
  size?: Size;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  helperText?: string;
  id?: string;
}

const props = withDefaults(defineProps<Props>(), {
  size: "md",
  disabled: false,
  required: false,
});

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
  blur: [event: FocusEvent];
  focus: [event: FocusEvent];
  change: [value: boolean];
}>();

const isFocused = ref(false);
const toggleId = computed(() => props.id || `toggle-${Math.random().toString(36).substr(2, 9)}`);

const wrapperClasses = computed(() => {
  const classes = ["ui-toggle"];
  classes.push(`ui-toggle--${props.size}`);
  if (props.disabled) classes.push("ui-toggle--disabled");
  if (props.error) classes.push("ui-toggle--error");
  return classes;
});

const toggleClasses = computed(() => {
  const classes = ["ui-toggle__input"];
  if (isFocused.value) classes.push("ui-toggle__input--focused");
  return classes;
});

const handleChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const value = target.checked;
  emit("update:modelValue", value);
  emit("change", value);
};

const handleBlur = (event: FocusEvent) => {
  isFocused.value = false;
  emit("blur", event);
};

const handleFocus = (event: FocusEvent) => {
  isFocused.value = true;
  emit("focus", event);
};

const handleLabelClick = (event: MouseEvent) => {
  if (props.disabled) {
    event.preventDefault();
  }
};
</script>

<style scoped>
.ui-toggle {
  display: flex;
  flex-direction: column;
  width: fit-content;
}

.ui-toggle__label {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #344054;
  user-select: none;
}

.ui-toggle--disabled .ui-toggle__label {
  cursor: not-allowed;
  opacity: 0.5;
}

.ui-toggle__input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.ui-toggle__switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
}

.ui-toggle__slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--color-border);
  transition: var(--transition-normal);
  border-radius: 24px;
}

.ui-toggle__slider:before {
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 2px;
  bottom: 2px;
  background-color: var(--color-bg-white);
  transition: var(--transition-normal);
  border-radius: 50%;
  box-shadow: var(--shadow-sm);
}

.ui-toggle__input:checked + .ui-toggle__switch .ui-toggle__slider {
  background-color: var(--color-primary);
}

.ui-toggle__input:checked + .ui-toggle__switch .ui-toggle__slider:before {
  transform: translateX(20px);
}

.ui-toggle__input:focus + .ui-toggle__switch .ui-toggle__slider {
  box-shadow: var(--ring) !important;
}

.ui-toggle__input:hover:not(:disabled) + .ui-toggle__switch .ui-toggle__slider {
  background-color: var(--color-text-muted);
}

.ui-toggle__input:checked:hover:not(:disabled) + .ui-toggle__switch .ui-toggle__slider {
  background-color: var(--color-primary-dark);
}

.ui-toggle--disabled .ui-toggle__switch .ui-toggle__slider {
  background-color: var(--color-border-light);
  cursor: not-allowed;
}

.ui-toggle--disabled .ui-toggle__input:checked + .ui-toggle__switch .ui-toggle__slider {
  background-color: var(--color-border);
}

.ui-toggle__text {
  font-size: 14px;
  font-weight: 500;
  color: #344054;
}

.ui-toggle__helper {
  margin-top: var(--space-1);
  font-size: var(--font-size-xs);
}

.ui-toggle__error {
  color: var(--color-danger);
}

.ui-toggle__helper-text {
  color: var(--color-text-placeholder);
}
</style>
